import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { recordReadWriteCount } from '../utils/withReadCounter';

export type FeedbackDevice = 'Android' | 'iPhone' | 'Laptop/Desktop' | 'Other';
export type FeedbackEase = 'very_easy' | 'easy' | 'neutral' | 'difficult' | 'very_difficult';
export type FeedbackPerformance = 'very_fast' | 'fast' | 'average' | 'slow';
export type FeedbackRecommendation = 'yes' | 'maybe' | 'no';

export interface FeedbackSubmissionInput {
  name: string;
  email: string;
  device: FeedbackDevice;
  otherDeviceDetails: string;
  appVersion: string;
  rating: number;
  encounteredBugs: boolean;
  bugDescription: string;
  mostUsedFeature: string;
  easeOfUse: FeedbackEase;
  confusingItems: string;
  performance: FeedbackPerformance;
  appCrashed: boolean;
  crashDetails: string;
  likedMost: string;
  improvements: string;
  futureFeatures: string;
  recommendation: FeedbackRecommendation;
  additionalComments: string;
}

export type SupportCategory = 'account' | 'login' | 'video' | 'safety' | 'technical' | 'other';

export interface SupportRequestInput {
  name: string;
  email: string;
  category: SupportCategory;
  subject: string;
  details: string;
  device: FeedbackDevice;
  otherDeviceDetails: string;
  appVersion: string;
}

const authenticatedUid = (): string => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Please sign in before sending a request.');
  return uid;
};

// Feedback and support use separate collections so their private payloads and
// Firestore validators remain independently maintainable.
export const submitFeedback = async (input: FeedbackSubmissionInput): Promise<void> => {
  const submitterUid = authenticatedUid();
  await addDoc(collection(db, 'feedbackSubmissions'), {
    schemaVersion: 1,
    ...input,
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    appVersion: input.appVersion.trim(),
    submitterUid,
    status: 'new',
    createdAt: serverTimestamp()
  });
  recordReadWriteCount('feedback-submissions', { writes: 1 });
};

export const submitSupportRequest = async (input: SupportRequestInput): Promise<void> => {
  const submitterUid = authenticatedUid();
  await addDoc(collection(db, 'supportRequests'), {
    schemaVersion: 1,
    ...input,
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    subject: input.subject.trim(),
    details: input.details.trim(),
    appVersion: input.appVersion.trim(),
    submitterUid,
    status: 'new',
    createdAt: serverTimestamp()
  });
  recordReadWriteCount('support-requests', { writes: 1 });
};
